// ─────────────────────────────────────────────────────────────────────────────
// CLAIM WATCH BOARD — three admin-only boxes on the agent Home: who is likely
// owed money on PRF, LRP and DRP for the most recent settled period.
//
// ⚠ THIS FILE LIVES IN src/admin/ AND MUST NEVER BE IMPORTED BY src/home/.
// src/home/homeCards.jsx renders in the CUSTOMER PORTAL as well as the agent
// app, and api/_home.js declares `contract.readsFarmerData:false` on the wire —
// tools/check-home-safe.js fails the build if that stops being true. This board
// reads the whole book by design. It is mounted only at the agent shell's
// nav==="home" branch, never at the portal's. tools/check-claim-watch.js
// asserts both halves.
//
// COLLAPSED BY DEFAULT AND DELIBERATELY NOT PERSISTED. Home exists so that
// opening the app with a customer or a prospect in the room does not put the
// book on screen; a list of other growers' names and claim amounts is exactly
// what that decision was avoiding. This follows the precedent the Data health
// control already set on this page — the compartment is never left open in
// front of somebody, because it re-collapses on every load. The collapsed
// header carries a COUNT of farms and no names, no dollars.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect } from "react";

const INK = "#1a2e1a", MID = "#2d4a2d", MUTED = "#60735f";
const LINE = "#dfe8df", CARD = "#ffffff";
const FLAG = "#a8620f";

const money = (n) =>
  n == null ? "—" : "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

// ⚠ IT READ ONCE AND THEN SAT THERE (Kevin, 2026-09-23: "Make sure Claim watch is
// live and will continuously update"). The effect fired when the panel was opened
// and never again, so a board left open all morning showed the book as it stood
// when it was unfolded — and this is a page an agent leaves open. Worse than
// stale: it carries no "as of", so there was nothing on screen to say which.
//
// Three rules, and the second is what stops this costing anything:
//   · POLL while it is open — REFRESH_MS, deliberately slow. This reads the whole
//     book across three products; a tight loop would be a real cost for a panel
//     nobody is staring at.
//   · NEVER POLL WHILE HIDDEN. `document.hidden` is checked at each tick and on
//     visibilitychange, so a background tab makes no requests at all. A laptop
//     shut overnight with Home open used to be a request every interval, all
//     night, for a screen nobody could see.
//   · REFETCH ON RETURN. The common case is not a long stare, it is coming back
//     to a tab the next morning — the one moment the figure on screen is most
//     likely to be wrong and least likely to be doubted. Guarded by
//     STALE_ON_FOCUS_MS so flicking between tabs does not refetch on every blur.
const REFRESH_MS = 120000;         // 2 minutes while open and visible
const STALE_ON_FOCUS_MS = 30000;   // on return, only if the last read is older than this

export function useClaimWatch(enabled) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  // WHEN the figures on screen were read. Rendered, because "live" is a claim and
  // a reader has to be able to check it.
  const [fetchedAt, setFetchedAt] = useState(null);
  const [tick, setTick] = useState(0);
  const refresh = React.useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) return;
    let dead = false;
    let timer = null;
    const hidden = () => (typeof document !== "undefined" && document.hidden);

    const read = () => {
      if (dead || hidden()) return;
      setLoading(true);
      const token = (() => { try { return localStorage.getItem("cloverag_access_token"); } catch (_) { return null; } })();
      fetch("/api/market?fn=claim-watch", token ? { headers: { Authorization: `Bearer ${token}` } } : undefined)
        .then(async (r) => {
          const j = await r.json().catch(() => ({}));
          if (!r.ok) throw new Error(j.error || `Claim Watch failed (${r.status})`);
          return j;
        })
        // ⚠ A FAILED REFRESH KEEPS THE LAST GOOD READ AND SAYS SO. Blanking the
        // board on a transient 5xx would replace a figure that was true two
        // minutes ago with nothing at all, which is the worse of the two.
        .then((j) => { if (!dead) { setData(j); setErr(null); setFetchedAt(Date.now()); } })
        .catch((e) => { if (!dead) setErr(e.message || String(e)); })
        .finally(() => { if (!dead) setLoading(false); });
    };

    read();
    timer = setInterval(read, REFRESH_MS);

    const onVisible = () => {
      if (hidden()) return;
      // Only when what is on screen has had time to go stale.
      setFetchedAt((at) => { if (at == null || Date.now() - at > STALE_ON_FOCUS_MS) read(); return at; });
    };
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisible);
    if (typeof window !== "undefined") window.addEventListener("focus", onVisible);

    return () => {
      dead = true;
      if (timer) clearInterval(timer);
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisible);
      if (typeof window !== "undefined") window.removeEventListener("focus", onVisible);
    };
  }, [enabled, tick]);

  return { data, err, loading, fetchedAt, refresh };
}

// "as of" in the reader's own clock. A board that claims to be live has to show
// when it last actually read something.
export function claimWatchAsOf(fetchedAt, now = Date.now()) {
  if (!fetchedAt) return null;
  const secs = Math.max(0, Math.round((now - fetchedAt) / 1000));
  if (secs < 45) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return mins + " min ago";
  const hrs = Math.round(mins / 60);
  return hrs + (hrs === 1 ? " hour ago" : " hours ago");
}

function Row({ farm, onSelectFarmer }) {
  const clickable = !!(onSelectFarmer && farm.farmerId);
  return (
    <div
      onClick={clickable ? () => onSelectFarmer(farm.farmerId) : undefined}
      style={{
        display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10,
        padding: "7px 0", borderBottom: `1px solid ${LINE}`,
        cursor: clickable ? "pointer" : "default",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ color: INK, fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {farm.name}
        </div>
        {farm.lines && farm.lines.length > 0 && (
          <div style={{ color: MUTED, fontSize: 11, marginTop: 2 }}>
            {farm.lines.slice(0, 2).map((l, i) => (
              <span key={i}>
                {i > 0 && " · "}
                {l.county ? `${l.county} ` : ""}
                {l.grid ? `grid ${l.grid} ` : ""}
                {l.index != null ? `index ${l.index}% ` : ""}
                {l.klass ? `Class ${l.klass} ` : ""}
                {l.commodity ? `${l.commodity} ` : ""}
                {l.endDate ? `ended ${l.endDate}` : ""}
              </span>
            ))}
            {farm.lines.length > 2 && ` · +${farm.lines.length - 2} more`}
          </div>
        )}
      </div>
      <div style={{ color: FLAG, fontWeight: 700, fontSize: 14, fontFamily: "monospace", whiteSpace: "nowrap" }}>
        {money(farm.outstanding)}
      </div>
    </div>
  );
}

export function Box({ board, onSelectFarmer }) {
  if (!board) return null;
  const total = (board.farms || []).reduce((s, f) => s + (f.outstanding || 0), 0);
  return (
    <div style={{ background: CARD, border: `1px solid ${LINE}`, borderRadius: 10, padding: "14px 16px", minWidth: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <div style={{ color: INK, fontWeight: 800, fontSize: 14, letterSpacing: 0.3 }}>{board.product}</div>
        <div style={{ color: MUTED, fontSize: 11 }}>{board.period || "—"}</div>
      </div>

      <div style={{ marginTop: 8, marginBottom: 10 }}>
        <div style={{ color: total > 0 ? FLAG : MUTED, fontWeight: 800, fontSize: 22, fontFamily: "monospace" }}>
          {money(total)}
        </div>
        <div style={{ color: MUTED, fontSize: 11 }}>
          {/* ⚠ THE SUBTITLE MUST NOT CLAIM NETTING THE PRODUCT DOES NOT DO. It read
              "outstanding after payments already made" on all three boxes while the PRF
              box's own footnote said "not netted — PRF payments carry a crop year and
              county but no interval". Two statements about the same figure, contradicting
              each other, on the same card. LRP and DRP genuinely net; PRF cannot, so it
              says what it is: the estimate before any payment is subtracted. */}
          {(board.farms || []).length === 0
            ? "no farm shows an outstanding claim"
            : `${board.farms.length} farm${board.farms.length === 1 ? "" : "s"} · ${
                /not netted/i.test(String(board.netting || "")) ? "estimated — payments not netted, see below" : "outstanding after payments already made"}`}
        </div>
      </div>

      {board.caveat && (
        <div style={{ background: "#fdf6ec", border: `1px solid #eddcc2`, borderRadius: 6, padding: "6px 8px", color: "#7a5310", fontSize: 11, marginBottom: 8 }}>
          {board.caveat}
        </div>
      )}

      {(board.farms || []).map((f, i) => <Row key={f.farmerId || i} farm={f} onSelectFarmer={onSelectFarmer} />)}

      {board.netting && (
        <div style={{ color: MUTED, fontSize: 10, marginTop: 8, lineHeight: 1.45 }}>{board.netting}</div>
      )}

      {/* Absences are NAMED, never omitted — a box that quietly shows nothing
          reads as "nobody has a claim" rather than "we could not price this". */}
      {(board.absences || []).map((a, i) => (
        <div key={i} style={{ color: MUTED, fontSize: 10, marginTop: 6, lineHeight: 1.45 }}>⚠ {a}</div>
      ))}

      {(board.unknown || []).length > 0 && (
        <details style={{ marginTop: 6 }}>
          <summary style={{ color: MUTED, fontSize: 10, cursor: "pointer" }}>
            {board.unknown.length} line{board.unknown.length === 1 ? "" : "s"} we could not price
          </summary>
          <div style={{ marginTop: 4 }}>
            {board.unknown.map((u, i) => (
              <div key={i} style={{ color: MUTED, fontSize: 10, lineHeight: 1.5 }}>
                {u.name} — {u.reason}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

export default function ClaimWatchBoard({ isMobile = false, onSelectFarmer = null }) {
  // Not persisted on purpose — see the header note. Every page load starts shut.
  const [open, setOpen] = useState(false);
  const { data, err, loading, fetchedAt, refresh } = useClaimWatch(open);
  // Re-renders the "as of" label while the panel sits open, so "just now" does not
  // stay on screen for an hour. Costs nothing — it triggers no request.
  const [, setClock] = useState(0);
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => setClock((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, [open]);
  const asOf = claimWatchAsOf(fetchedAt);

  const boards = data ? [data.prf, data.lrp, data.drp].filter(Boolean) : [];
  const farmCount = boards.reduce((s, b) => s + ((b.farms || []).length), 0);

  return (
    <div style={{ marginBottom: isMobile ? 16 : 22 }}>
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
          background: CARD, border: `1px solid ${LINE}`, borderRadius: 10,
          padding: isMobile ? "10px 12px" : "11px 16px", cursor: "pointer",
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, minWidth: 0 }}>
          <span style={{ color: INK, fontWeight: 800, fontSize: 13, letterSpacing: 0.3 }}>CLAIM WATCH</span>
          <span style={{ color: MUTED, fontSize: 11 }}>
            likely PRF · LRP · DRP indemnities, most recent settled period
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, whiteSpace: "nowrap" }}>
          {open && data && (
            <span style={{ color: farmCount ? FLAG : MUTED, fontSize: 11, fontWeight: 700 }}>
              {farmCount} farm{farmCount === 1 ? "" : "s"}
            </span>
          )}
          {/* ⚠ "LIVE" IS A CLAIM AND THE PANEL HAS TO SHOW ITS WORKING. The board
              refreshes itself every two minutes while it is open and visible; the
              stamp is how a reader tells a live figure from one that stopped
              updating because the tab was hidden or a refresh failed. */}
          {open && asOf && (
            <span title={"Claim Watch re-reads the book every two minutes while this panel is open and the tab is visible, and again when you come back to it. Last read " + new Date(fetchedAt).toLocaleTimeString() + "."}
              style={{ color: MUTED, fontSize: 10.5, cursor: "help" }}>
              {loading ? "refreshing…" : "as of " + asOf}
            </span>
          )}
          {open && (
            <span onClick={(e) => { e.stopPropagation(); refresh(); }}
              title="Read the book again now"
              style={{ color: MID, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>↻</span>
          )}
          <span style={{ color: MID, fontSize: 11, fontWeight: 700 }}>{open ? "Hide" : "Show"}</span>
        </div>
      </div>

      {open && (
        <div style={{ marginTop: 10 }}>
          {loading && !data && <div style={{ color: MUTED, fontSize: 12, padding: "8px 2px" }}>Reading the book…</div>}
          {err && (
            <div style={{ background: "#fdecea", border: "1px solid #f5c6c2", borderRadius: 8, padding: "9px 12px", color: "#8c1d18", fontSize: 12 }}>
              Couldn't load Claim Watch: {err}
            </div>
          )}
          {data && (
            <>
              <div style={{ display: "grid", gap: 12, gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))" }}>
                {boards.map((b, i) => <Box key={i} board={b} onSelectFarmer={onSelectFarmer} />)}
              </div>
              {(data.notes || []).map((n, i) => (
                <div key={i} style={{ color: MUTED, fontSize: 10, marginTop: 8 }}>⚠ {n}</div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
